"use client";

/**
 * CancelOrderSheet — destructive cancel confirmation as a responsive
 * bottom-sheet (mobile) / centered card (desktop).
 *
 * Uses the shared ModalSheet for the slide-up / centered-modal behaviour.
 * Collects a cancelReasonCode before forwarding to the caller's onConfirm.
 */

import { useState } from "react";
import { ModalSheet } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { CancelReasonCode, SellerOrderDetail } from "@/lib/api/types";
import { formatEgp } from "@/modules/orders/lib/order-money";

/* ── Types ─────────────────────────────────────────────────────────────── */

export type CancelOrderSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  order: SellerOrderDetail;
  onConfirm: (reasonCode: CancelReasonCode, note?: string) => void;
  isSubmitting: boolean;
};

/* ── Reason chips config ────────────────────────────────────────────────── */

const REASON_CHIPS: ReadonlyArray<{
  code: CancelReasonCode;
  labelKey: string;
}> = [
  { code: "customer_requested", labelKey: keys.orders.cancelReasonCustomerRequested },
  { code: "out_of_stock", labelKey: keys.orders.cancelReasonOutOfStock },
  { code: "cannot_deliver", labelKey: keys.orders.cancelReasonCannotDeliver },
  { code: "other", labelKey: keys.orders.cancelReasonOther },
];

/* ── Component ──────────────────────────────────────────────────────────── */

export function CancelOrderSheet({
  isOpen,
  onClose,
  order,
  onConfirm,
  isSubmitting,
}: CancelOrderSheetProps) {
  const t = useTranslations();

  // Default to the first chip pre-selected.
  const [selectedCode, setSelectedCode] =
    useState<CancelReasonCode>("customer_requested");

  // Determine whether there is an online refund to announce.
  // paidAmount > 0 AND paymentMethod is not COD → buyer paid online.
  const hasOnlineRefund =
    order.paymentMethod !== "COD" && order.paidAmount > 0;
  const refundAmount = hasOnlineRefund ? formatEgp(order.paidAmount) : null;

  const handleConfirm = () => {
    if (isSubmitting) return;
    onConfirm(selectedCode);
  };

  return (
    <ModalSheet isOpen={isOpen} onClose={onClose} size="md" elevated>
      {/* Scrollable inner area */}
      <div className="flex min-h-0 flex-col gap-5 overflow-y-auto px-5 pb-5 pt-4">

        {/* Warning circle icon */}
        <div className="flex justify-center">
          <div
            className="flex h-[52px] w-[52px] items-center justify-center rounded-[14px]"
            style={{ backgroundColor: "#fde7d3" }}
          >
            <AlertTriangleIcon />
          </div>
        </div>

        {/* Title */}
        <div className="text-center">
          <h2
            className="text-[18px] font-bold leading-snug"
            style={{ color: "#13171f" }}
          >
            {t(keys.orders.cancelSheetTitle)}
          </h2>
        </div>

        {/* Body copy */}
        <p
          className="text-center text-[13px] leading-relaxed"
          style={{ color: "#6f6a5e" }}
        >
          {hasOnlineRefund && refundAmount ? (
            <CancelBodyRefund amount={refundAmount} t={t} />
          ) : (
            t(keys.orders.cancelSheetBodyNoRefund)
          )}
        </p>

        {/* Reason section */}
        <div className="flex flex-col gap-2.5">
          {/* Eyebrow */}
          <span
            className="text-[11px] font-bold uppercase tracking-[0.06em]"
            style={{ color: "#9a9486" }}
          >
            {t(keys.orders.cancelReasonEyebrow)}
          </span>

          {/* Chips */}
          <div className="flex flex-wrap gap-2">
            {REASON_CHIPS.map(({ code, labelKey }) => {
              const isSelected = selectedCode === code;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => setSelectedCode(code)}
                  className="rounded-full px-3.5 py-2 text-[13px] font-medium transition-colors"
                  style={
                    isSelected
                      ? { backgroundColor: "#13171f", color: "#ffffff", border: "1px solid #13171f" }
                      : { backgroundColor: "#ffffff", color: "#3a3f4a", border: "1px solid #e3ddd0" }
                  }
                >
                  {t(labelKey)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2.5">
          {/* Primary destructive button */}
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleConfirm}
            className="h-[50px] w-full rounded-[13px] text-[15px] font-bold text-white transition-colors disabled:opacity-60"
            style={{ backgroundColor: isSubmitting ? "#dc2626" : "#dc2626" }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#b91c1c";
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#dc2626";
            }}
          >
            {isSubmitting
              ? t(keys.orders.savingAction)
              : hasOnlineRefund
                ? t(keys.orders.cancelConfirmRefund)
                : t(keys.orders.cancelConfirmNoRefund)}
          </button>

          {/* Secondary ghost button */}
          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-[50px] w-full rounded-[13px] border text-[15px] font-medium transition-colors disabled:opacity-60"
            style={{
              backgroundColor: "#ffffff",
              borderColor: "#e3ddd0",
              color: "#13171f",
            }}
          >
            {t(keys.orders.keepOrder)}
          </button>
        </div>
      </div>
    </ModalSheet>
  );
}

/* ── Body copy with bold amount ─────────────────────────────────────────── */

/**
 * Renders the refund body copy by splitting the raw template string on
 * `{amount}` and injecting a bold span for the amount value.
 * The template lives in the locale file and contains `{amount}` as a
 * literal placeholder; we split on it rather than calling t() with params
 * so we can wrap the amount in a <strong> element.
 */
function CancelBodyRefund({
  amount,
  t,
}: {
  amount: string;
  t: ReturnType<typeof useTranslations>;
}) {
  const template = t(keys.orders.cancelSheetBodyRefund, { amount: "\x00" });
  const parts = template.split("\x00");
  const before = parts[0] ?? "";
  const after = parts[1] ?? "";
  return (
    <>
      {before}
      <strong style={{ color: "#13171f" }}>{amount}</strong>
      {after}
    </>
  );
}

/* ── Alert triangle icon ────────────────────────────────────────────────── */

function AlertTriangleIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#d97706"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
